<template>
    <div class="baggage-selection-v2">
        <template v-if="hasSections">
            <BaggageSmallBag v-if="smallBagIncluded" />
            <Section
                icon="CabinBag"
                :type="BaggageType.CabinBag"
                :onSelection="onSelection"
                :splitAlternatives="splitAlternatives"
                :splitIncluded="splitIncluded"
                :options="cabinBagOptions"
                :included="cabinBagIncluded"
                :showLabels="showLabels"
                :isOneWay="isOneWay"
                :noBaggageSelectionCabinBagLabel="noBaggageSelectionCabinBagLabel"
                :noBaggageSelectionCheckedBagLabel="noBaggageSelectionCheckedBagLabel"
                :noBaggageSelectionLabel="noBaggageSelectionLabel"
                :title="t({ key: 'shared.hand_baggage_title' })"
                :description="
                    t({
                        key: 'shared.hand_baggage_description',
                    })
                " />

            <Section
                icon="CheckedBag"
                :type="BaggageType.CheckedBag"
                :onSelection="onSelection"
                :splitAlternatives="splitAlternatives"
                :splitIncluded="splitIncluded"
                :options="checkedBagOptions"
                :included="checkedBagIncluded"
                :showLabels="showLabels"
                :isOneWay="isOneWay"
                :noBaggageSelectionCabinBagLabel="noBaggageSelectionCabinBagLabel"
                :noBaggageSelectionCheckedBagLabel="noBaggageSelectionCheckedBagLabel"
                :noBaggageSelectionLabel="noBaggageSelectionLabel"
                :title="t({ key: 'shared.checked_baggage_title' })"
                :description="
                    t({
                        key: 'shared.checked_baggage_description',
                    })
                " />
        </template>
        <div v-else class="baggage-section">
            <Info
                icon="NoBag"
                :title="t({ key: 'shared.no_baggage_title' })"
                :description="t({ key: 'shared.no_baggage_description' })" />
        </div>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, PropType, provide } from 'vue';
    import { t } from '@shared/helpers/translation';
    import { isEmpty } from 'lodash';
    import { Baggage, BaggageIncluded, BaggageType } from '@shared/interfaces/baggage';
    import { NoSelectionType, SelectionMetaData } from './BaggageSelectionForm.interfaces';
    import Info from './Shared/Info.vue';
    import Section from './Sections/Section.vue';
    import BaggageSmallBag from '@ui/components/BaggageSelectionForm/BaggageSmallBag.vue';

    export default defineComponent({
        name: 'BaggageSelectionForm',
        components: { Section, Info, BaggageSmallBag },
        methods: { t },
        props: {
            splitAlternatives: { type: Boolean, default: false },
            splitIncluded: { type: Boolean, default: false },
            isOneWay: { type: Boolean, default: false },
            paxIndex: { type: Number, required: true },
            options: { type: Array as PropType<Baggage[]>, default: () => [] },
            smallBagIncluded: { type: Boolean, default: false },
            included: {
                type: Array as PropType<BaggageIncluded[]>,
                default: () => [],
            },
            onSelect: {
                required: true,
                type: Function as PropType<
                    (
                        selection: Baggage | NoSelectionType,
                        ids: string[],
                        meta: SelectionMetaData
                    ) => void
                >,
            },
            noBaggageSelectionCabinBagLabel: {
                type: String,
                default: () => t({ key: 'shared.no_baggage_selection_cabin_bag' }),
            },
            noBaggageSelectionCheckedBagLabel: {
                type: String,
                default: () => t({ key: 'shared.no_baggage_selection_checked_bag' }),
            },
            noBaggageSelectionLabel: {
                type: String,
                default: () => t({ key: 'shared.no_baggage_selection' }),
            },
        },
        setup(props) {
            // TODO: Instead of passing down all props, we can change paxIndex below to pass the props object instead. To not prop drill..
            provide('paxIndex', props.paxIndex);

            const getBaggagesByType = (baggages: Baggage[], type: BaggageType) => {
                return [...baggages].filter((x) => x.Type === type);
            };

            const getBaggagesIncludedByType = (
                baggages: BaggageIncluded[],
                type: BaggageType
            ) => {
                return [...baggages].filter((x) => x.Type === type);
            };

            const hasSections = computed(
                (): boolean => !isEmpty(props.options) || !isEmpty(props.included)
            );

            const cabinBagOptions = computed((): Baggage[] =>
                getBaggagesByType(props.options, BaggageType.CabinBag)
            );

            const checkedBagOptions = computed((): Baggage[] =>
                getBaggagesByType(props.options, BaggageType.CheckedBag)
            );

            const cabinBagIncluded = computed((): BaggageIncluded[] =>
                getBaggagesIncludedByType(props.included, BaggageType.CabinBag)
            );

            const checkedBagIncluded = computed((): BaggageIncluded[] =>
                getBaggagesIncludedByType(props.included, BaggageType.CheckedBag)
            );

            const showLabels = computed((): boolean => {
                const hasCabinBagOptions = cabinBagOptions.value?.length > 0;
                const hasCheckedBagOptions = checkedBagOptions.value?.length > 0;

                const hasCabinBagIncluded = cabinBagIncluded.value?.length > 0;
                const hasCheckedBagIncluded = checkedBagIncluded.value?.length > 0;

                return (
                    (props.splitAlternatives &&
                        (hasCabinBagOptions || hasCheckedBagOptions)) ||
                    (props.splitIncluded && (hasCabinBagIncluded || hasCheckedBagIncluded))
                );
            });

            const onSelection = (
                selection: Baggage | NoSelectionType,
                ids: string[],
                meta: SelectionMetaData
            ) => props.onSelect && props.onSelect(selection, ids, meta);

            return {
                hasSections,
                showLabels,
                cabinBagOptions,
                checkedBagOptions,
                cabinBagIncluded,
                checkedBagIncluded,
                onSelection,
                BaggageType,
            };
        },
    });
</script>

<style lang="scss">
    @forward './BaggageSelectionForm.scss';
</style>
